import { useNavigate } from 'react-router-dom'
import { cn } from '@/common/utils/cn'
import { formatDate } from '@/common/utils/formatters'
import { ManualEntryProposalCardItem } from '../../types/maintenanceManualEntryProposal.type'
import {
  proposalFinalStateAccentClass,
  proposalFinalStateBannerClass,
  proposalFinalStateIcon,
  proposalFinalStateLabel,
  proposalRejectedByLabel,
} from '../../constants/proposalStatus.constant'
import { timeAgo } from '../../utils/timeAgo'

type Props = {
  data: ManualEntryProposalCardItem
  accentClassName?: string
}

const finalStateDetail = (data: ManualEntryProposalCardItem): string | null => {
  if (data.finalState === 'cancelled') {
    return 'Proposta cancelada'
  }

  if (data.finalState === 'rejected' && data.rejectedBy) {
    const when = data.finalizedAt ? ` ${timeAgo(data.finalizedAt)}` : ''
    return `${proposalRejectedByLabel[data.rejectedBy]}${when}`
  }

  return null
}

const ProposalCard = ({ data, accentClassName = 'bg-primary-blue' }: Props) => {
  const { forwardingType, equipmentModel, companyName, createdAt, finalState } = data

  const navigate = useNavigate()

  const accent = finalState ? proposalFinalStateAccentClass[finalState] : accentClassName
  const FinalStateIcon = finalState ? proposalFinalStateIcon[finalState] : null

  const detailLabel = finalStateDetail(data)

  return (
    <div
      data-testid={`proposal_card_${data.id}`}
      onClick={() => navigate(`/maintenance-proposals/${data.id}`)}
      className="flex bg-white-100 border border-border-default rounded-lg overflow-hidden shrink-0 cursor-pointer transition-all duration-200 hover:-translate-y-0.5 hover:border-primary-blue hover:shadow-[0px_6px_16px_rgba(0,0,0,0.35)]"
    >
      <div className={cn('w-1 shrink-0', accent)} />

      <div className="flex flex-1 min-w-0 flex-col gap-2 p-3">
        <div className="flex items-center gap-1.5 min-w-0 text-[10px] italic leading-tight text-white-300">
          <span className="truncate">{forwardingType}</span>
        </div>

        <div className="flex items-baseline gap-2">
          <p className="flex-1 min-w-0 truncate text-xs font-bold" title={equipmentModel}>
            {equipmentModel}
          </p>
          <p className="flex-1 min-w-0 truncate text-right text-[11px] text-white-400" title={companyName}>
            {companyName}
          </p>
        </div>

        {finalState && FinalStateIcon && (
          <div
            className={cn(
              'flex items-center gap-1.5 rounded-md px-2 py-1 min-w-0',
              proposalFinalStateBannerClass[finalState],
            )}
          >
            <FinalStateIcon size={13} strokeWidth={2} className="shrink-0" />
            <span className="text-[10px] font-bold shrink-0">{proposalFinalStateLabel[finalState]}</span>
            {detailLabel && (
              <span className="truncate text-[10px] font-normal opacity-70" title={detailLabel}>
                · {detailLabel}
              </span>
            )}
          </div>
        )}

        <span className="text-[10px] text-white-300">
          Criada em {formatDate(new Date(createdAt))}
        </span>
      </div>
    </div>
  )
}

export default ProposalCard
